import { useState } from 'react';
import { c, card, tbl } from '../../theme';
import { fmtClock, fmtCount, fmtElapsed } from './format';
import SingleSelect from './SingleSelect';
import { languageOptions, matchesLanguage } from './language';

// The floor's agents now: status, time in it, and today's Handled and AHT.
// Offline agents fold into a line at the bottom. A row is marked when AHT or
// time available passes the flag (the old page's outlier rule).

export const STATUS_LABEL = {
  available: 'On queue, available',
  interacting: 'Interacting',
  not_responding: 'Not responding',
  break: 'Break',
  meal: 'Lunch',
  off_queue: 'Off queue',
  away: 'Away',
  offline: 'Offline',
};
const STATUS_TONE = {
  available: [c.successTint, c.successText],
  interacting: [c.infoTint, c.navy],
  not_responding: [c.warningTint, c.warningText],
  break: [c.warningTint, c.warningText],
  meal: [c.dangerTint, c.dangerText],
};
const TABS = [
  ['all', 'All', () => true],
  ['on', 'On queue', (a) => ['available', 'interacting', 'not_responding'].includes(a.status)],
  ['available', 'Available', (a) => a.status === 'available'],
  ['break', 'Break or lunch', (a) => a.status === 'break' || a.status === 'meal'],
  ['off', 'Off queue', (a) => a.status === 'off_queue' || a.status === 'away'],
];

// The Genesys WFM activity an agent is scheduled for now, as the old roster
// named them. Red "Should be" when the server's rule says they are not doing
// it (ccLiveRead.scheduleMismatch).
const SCHEDULED_LABEL = {
  OnQueueWork: 'On Queue', Break: 'Break', Meal: 'Meal', Meeting: 'Meeting', Training: 'Training',
  OffQueueWork: 'Off Queue', TimeOff: 'Time Off', Unavailable: 'Unavailable',
};
function Scheduled({ a }) {
  if (!a.scheduled || a.scheduled === 'Unscheduled') return <span style={s.sub}>No schedule</span>;
  const label = SCHEDULED_LABEL[a.scheduled] || a.scheduled;
  if (a.scheduleMismatch) {
    return <span style={{ ...s.pill, background: c.dangerTint, color: c.dangerText }} title={`Scheduled: ${label}; what they are doing differs`}>Should be: {label}</span>;
  }
  return <span style={{ ...s.pill, background: c.surfaceSunk, color: c.textMuted }} title={`Scheduled: ${label}`}>{label}</span>;
}

// Time in a status, as the old roster showed it: "41s", "1m 23s", "1h 5m".
export const fmtSince = fmtElapsed;

export default function LiveRoster({ roster, thresholds }) {
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  // The old Live page filtered the floor by language as well as by status: on
  // a Spanish queue backing up, the question is who is on shift who can take
  // it, and the status tabs alone cannot answer it.
  const [language, setLanguage] = useState('');
  const onShift = roster.filter((a) => a.status !== 'offline');
  const offline = roster.filter((a) => a.status === 'offline');
  const needle = search.trim().toLowerCase();
  const pass = TABS.find(([k]) => k === tab)[2];
  const rows = onShift.filter((a) => pass(a) && matchesLanguage(a, language)
    && (!needle || (a.name || '').toLowerCase().includes(needle)));

  return (
    <section style={s.card} aria-label="Team roster">
      <div style={s.head}>
        <h2 style={s.h2}>Team roster · {onShift.length} on shift</h2>
        <div style={s.tabs} role="tablist" aria-label="Agent status">
          {TABS.map(([k, label, fn]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} style={tab === k ? s.tabOn : s.tab}>
              {label} <span style={s.count}>{onShift.filter(fn).length}</span>
            </button>
          ))}
        </div>
        <SingleSelect label="Language" options={languageOptions(roster)} value={language}
          onChange={setLanguage} allLabel="All languages" />
        <input id="cc-live-search" type="search" aria-label="Search agents" placeholder="Search agents" value={search}
          onChange={(e) => setSearch(e.target.value)} style={s.input} />
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Agent</th><th style={s.th}>Status</th><th style={s.th}>Scheduled</th><th style={s.thNum}>In status</th>
              <th style={s.thNum}>Handled today</th><th style={s.thNum}>AHT today</th><th style={s.th}>Pod</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => {
              const [bg, fg] = STATUS_TONE[a.status] || [c.surfaceSunk, c.textStrong];
              const flagged = a.flags.longAht || a.flags.longIdle;
              return (
                <tr key={a.userId} style={flagged ? s.flagRow : s.row}>
                  <td style={{ ...s.td, fontWeight: 550 }}>{a.name || 'Unknown agent'}</td>
                  <td style={s.td}>
                    <span style={{ ...s.pill, background: bg, color: fg }}>{STATUS_LABEL[a.status]}</span>
                    {a.presence && !['available', 'interacting'].includes(a.status) && a.presence !== STATUS_LABEL[a.status] && (
                      <span style={s.sub}> {a.presence}</span>
                    )}
                  </td>
                  <td style={s.td}><Scheduled a={a} /></td>
                  <td style={{ ...s.num, color: a.flags.longIdle ? c.dangerText : s.num.color }}
                    title={a.flags.longIdle ? `Available longer than ${Math.round(thresholds.idleMaxSecs / 60)} minutes` : undefined}>
                    {fmtSince(a.secondsInStatus)}
                  </td>
                  <td style={s.num}>{fmtCount(a.handled)}</td>
                  <td style={{ ...s.num, color: a.flags.longAht ? c.dangerText : s.num.color }}
                    title={a.flags.longAht ? `AHT above ${fmtClock(thresholds.ahtMaxSecs)}` : undefined}>
                    {fmtClock(a.aht)}{a.stuckExcludedSecs > 0 ? ' ⚑' : ''}
                  </td>
                  <td style={s.td}>{a.pod}</td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={7} style={s.empty}>No agents in this view.</td></tr>}
          </tbody>
        </table>
      </div>
      {offline.length > 0 && (
        <details style={s.offline}>
          <summary>Offline ({offline.length})</summary>
          <p style={s.offList}>{offline.map((a) => a.name || 'Unknown agent').join(' · ')}</p>
        </details>
      )}
      <p style={s.foot}>
        Red marks AHT today above {fmtClock(thresholds.ahtMaxSecs)} or available longer than {Math.round(thresholds.idleMaxSecs / 60)} minutes.
        An agent&rsquo;s pod is where they handled most this month. ⚑ handle time adjusted for a leg Genesys left open.
      </p>
    </section>
  );
}

const s = {
  card:    { ...card.base, padding: '14px 0 10px' },
  head:    { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', margin: '0 18px 10px' },
  h2:      { fontSize: '13px', fontWeight: 650, color: c.text, margin: 0 },
  tabs:    { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  tab:     { all: 'unset', cursor: 'pointer', fontSize: '12px', padding: '4px 10px', borderRadius: '999px', color: c.textMuted, border: `1px solid ${c.border}` },
  tabOn:   { all: 'unset', cursor: 'pointer', fontSize: '12px', padding: '4px 10px', borderRadius: '999px', color: '#fff', background: c.navy, border: `1px solid ${c.navy}` },
  count:   { opacity: 0.75, marginLeft: '2px' },
  input:   { fontSize: '12px', padding: '5px 8px', border: `1px solid ${c.borderStrong}`, borderRadius: '6px', fontFamily: 'inherit', color: c.textStrong, background: c.surface, marginLeft: 'auto' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th:      { ...tbl.th, textAlign: 'left' },
  thNum:   { ...tbl.thR, whiteSpace: 'nowrap' },
  td:      { ...tbl.td, color: c.textStrong, whiteSpace: 'nowrap' },
  num:     { ...tbl.tdR, color: c.textStrong },
  row:     { ...tbl.tr },
  flagRow: { ...tbl.tr, boxShadow: `inset 3px 0 0 ${c.dangerText}` },
  pill:    { fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px' },
  sub:     { fontSize: '11px', color: c.textMuted },
  empty:   { ...tbl.td, textAlign: 'center', color: c.textMuted, padding: '20px' },
  offline: { margin: '10px 18px 0', fontSize: '12px', color: c.textMuted },
  offList: { margin: '6px 0 0', lineHeight: 1.6 },
  foot:    { fontSize: '11px', color: c.textMuted, margin: '8px 18px 0', lineHeight: 1.45 },
};
